import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  StarIcon,
  LinkIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import QRCode from 'react-qr-code';
import { useGoogleReviewConfig } from './useGoogleReviewConfig';
import { validateGoogleReviewUrl } from './validateGoogleReviewUrl';
import { buildReviewPageUrl } from './buildReviewPageUrl';
import GoogleReviewFeedbackPanel from './GoogleReviewFeedbackPanel';

export default function GoogleReviewSettingsPanel({ clinicId }) {
  const { loading, config, isReady, saveConfig, reload } = useGoogleReviewConfig(clinicId);
  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    setEnabled(config.enabled);
    setUrl(config.googleReviewUrl || '');
  }, [config.enabled, config.googleReviewUrl]);

  const reviewPageUrl = clinicId ? buildReviewPageUrl(clinicId) : '';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUrlError('');
    if (enabled) {
      const check = validateGoogleReviewUrl(url);
      if (!check.valid) {
        setUrlError(check.error);
        return;
      }
    }
    setSaving(true);
    try {
      await saveConfig({
        enabled,
        googleReviewUrl: enabled ? url.trim() : config.googleReviewUrl,
      });
      await reload();
      showToast(enabled ? 'Google reviews enabled' : 'Google reviews disabled');
    } catch (err) {
      showToast(err?.message || 'Could not save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reviewPageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Review page link copied');
    } catch {
      showToast('Could not copy link', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {toast ? (
        <div
          className={`rounded-lg px-4 py-3 text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {toast.message}
        </div>
      ) : null}

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <StarIcon className="h-5 w-5 text-amber-500" />
          Google reviews
        </h3>
        <p className="text-sm text-gray-600 max-w-2xl">
          Paste your practice Google review link once. Staff can then send review requests from{' '}
          <Link to="/clinic/consents" className="text-blue-600 hover:text-blue-800 font-medium">
            My Consents
          </Link>{' '}
          on any signed consent — patient email is filled in automatically.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-5 shadow-sm">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span>
            <span className="font-medium text-gray-900 block">Enable Google review requests</span>
            <span className="text-sm text-gray-500">
              When off, the send button on My Consents stays disabled.
            </span>
          </span>
        </label>

        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-800 mb-1.5">
            <LinkIcon className="h-4 w-4 text-amber-600" />
            Google review link
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setUrlError('');
            }}
            disabled={!enabled}
            placeholder="https://g.page/r/your-practice/review"
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-200 disabled:bg-gray-50 disabled:text-gray-500"
          />
          {urlError ? <p className="mt-1.5 text-sm text-red-600">{urlError}</p> : null}
          <p className="mt-2 text-xs text-gray-500">
            Google Business Profile → Ask for reviews → copy your location link.
          </p>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
        >
          <CheckCircleIcon className="h-5 w-5" />
          {saving ? 'Saving…' : 'Save review settings'}
        </button>
      </div>

      {isReady ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-950 mb-4">
            <StarSolidIcon className="h-5 w-5 text-amber-600" />
            Active — staff can send from My Consents
          </div>
          <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-start">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Patient review page (for QR / sharing)
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  readOnly
                  value={reviewPageUrl}
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-gray-600">QR for reception</p>
              <div className="rounded-xl bg-white p-3 border border-gray-200">
                <QRCode value={reviewPageUrl} size={112} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <GoogleReviewFeedbackPanel clinicId={clinicId} isReady={isReady} />
    </div>
  );
}
